/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import * as queryString from "query-string";
import { When } from "react-if";
import { Link, useParams } from "react-router-dom";
import { Select, Spacer, Text, theme } from "@nordcloud/gnui";
import { EnvironmentType, KpiTimePeriod } from "~/generated/graphql";
import {
  BrickLoader,
  CurrentTarget,
  KpiDashboardChart,
  KpiWidgets,
  UniversalWrap,
} from "~/components";
import { KpiType } from "~/constants";
import { useQueryState } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { getFirstItem, Maybe } from "~/tools";
import { RouteParams } from "~/views/environments/types";
import { useEnvironmentBusinessContexts, useEnvironmentKpis } from "./hooks";

type KpiQueryState = {
  kpiType?: Exclude<KpiType, KpiType.COST_ALLOCATION_COVERAGE>;
  businessContext?: string;
  period?: KpiTimePeriod;
};

type Props = {
  environmentType: Maybe<EnvironmentType>;
};

export function KpiDetailsTab({ environmentType }: Props) {
  const { environment = "" } = useParams<RouteParams>();

  const {
    state: {
      kpiType = KpiType.OVERALL_WASTE,
      businessContext,
      period = KpiTimePeriod.ThreeMonths,
      ...rest
    },
    set,
    setQueryState,
  } = useQueryState<KpiQueryState>();

  const { businessContexts, loading, error } = useEnvironmentBusinessContexts({
    id: environment,
  });

  // Choose Business Context in order: selected -> primary -> first item on list
  const selectedBusinessContext = React.useMemo(
    () =>
      businessContexts.find(({ value }) => businessContext === value) ??
      businessContexts.find(({ isPrimary }) => isPrimary) ??
      getFirstItem(businessContexts),
    [businessContexts, businessContext]
  );

  const { kpiLoading, kpiError, summary, ...kpis } = useEnvironmentKpis({
    bcId: selectedBusinessContext?.value ?? "",
    envId: environment,
    timePeriod: period,
    kpiType,
    environmentType,
  });

  React.useEffect(() => {
    if (
      environmentType === EnvironmentType.Production &&
      kpiType === KpiType.NON_PROD_HOURS
    ) {
      set("kpiType")(KpiType.OVERALL_WASTE);
    }
  }, [environmentType, kpiType, set]);

  const bcId = selectedBusinessContext?.value ?? "";
  const emptyTargetLink = `${ROUTES.finopsKpi.index}?${queryString.stringify({
    businessContext: bcId,
    kpiType,
  })}`;

  const allowedWidgets = [
    KpiType.OVERALL_WASTE,
    ...(environmentType === EnvironmentType.Production
      ? []
      : [KpiType.NON_PROD_HOURS]),
  ];

  return (
    <UniversalWrap
      loaderProps={{
        loading,
        Component: BrickLoader,
        viewBox: "0 0 10 1",
      }}
      errorProps={{
        error,
      }}
    >
      <Select
        name="Business context"
        options={businessContexts}
        value={selectedBusinessContext}
        onChange={(selectedBc) =>
          setQueryState({
            businessContext: selectedBc?.value,
            kpiType,
            period,
            ...rest,
          })
        }
      />
      <Spacer height={theme.spacing.spacing04} />
      <UniversalWrap
        loaderProps={{
          loading: summary == null,
          Component: BrickLoader,
          viewBox: "0 0 10 1",
        }}
        errorProps={{
          error: kpiError,
        }}
      >
        <KpiWidgets
          nonProdHours={summary.nonProdHours}
          overallWaste={summary.overallWaste}
          allowedWidgets={allowedWidgets}
          selectedType={kpiType}
          onClick={(type) => set("kpiType")(type)}
        />
      </UniversalWrap>
      <Spacer height={theme.spacing.spacing04} />
      <KpiDashboardChart
        data={kpis[kpiType]}
        kpiType={kpiType}
        loading={kpiLoading}
        period={period}
        setPeriod={(input) => set("period")(input)}
      />
      <Spacer height={theme.spacing.spacing04} />
      <When
        condition={kpis[kpiType]?.currentTarget != null && kpiLoading === false}
      >
        <CurrentTarget
          targetData={kpis[kpiType]?.currentTarget}
          loading={kpiLoading}
          kpiType={kpiType}
          editable={false}
        />
      </When>
      <When
        condition={kpis[kpiType]?.currentTarget == null && kpiLoading === false}
      >
        <Text color={theme.colors.danger} mb={0}>
          You can create new target on{" "}
          <Link to={emptyTargetLink}>KPI Page</Link>
        </Text>
      </When>
    </UniversalWrap>
  );
}
